import React, { useEffect, useState } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  AlertTitle,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  FormGroup,
  LinearProgress,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
  Sync as SyncIcon,
  CloudDownload as CloudDownloadIcon,
  Storage as StorageIcon,
  Home as HomeIcon,
  CloudSync as CloudSyncIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../../../utils/config';
import { useAuthenticatedFetch } from '../../../hooks/useAuthenticatedFetch';

interface ConnectionStatus {
  success: boolean;
  connected: boolean;
  verified?: boolean;
  requiresReconnect?: boolean;
  companyId?: string;
  connectedAt?: string;
  lastUpdated?: string;
  refreshTokenExpiresAt?: string;
  accessTokenExpired?: boolean;
  message?: string;
}

interface BarrettConnectionStatus {
  success: boolean;
  connected: boolean;
  verified?: boolean;
  requiresReconnect?: boolean;
  companyId?: string;
  connectedAt?: string;
  lastUpdated?: string;
  refreshTokenExpiresAt?: string;
  accessTokenExpired?: boolean;
  message?: string;
}

interface SyncStatus {
  syncId: string;
  status: 'in_progress' | 'completed' | 'failed' | 'partial';
  startedAt: string;
  completedAt?: string;
  progress: {
    totalEntities: number;
    completedEntities: number;
    currentEntity?: string;
    totalRecords?: number;
    syncedRecords?: number;
    failedRecords?: number;
  };
  errors?: Array<{
    entity: string;
    message: string;
    count: number;
  }>;
}

/**
 * QuickBooks Admin Page
 * Displays the current status of the QuickBooks integration
 */
export const QuickBooksPage: React.FC = () => {
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [loading, setLoading] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus | null>(null);
  const [barrettConnectionStatus, setBarrettConnectionStatus] = useState<BarrettConnectionStatus | null>(null);
  const [barrettLoading, setBarrettLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [syncType, setSyncType] = useState<'full' | 'incremental'>('incremental');
  const [selectedEntities, setSelectedEntities] = useState<string[]>([]);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);

  const availableEntities = [
    'Customer', 'Vendor', 'Invoice', 'Bill', 'Payment', 
    'BillPayment', 'Item', 'Account', 'Department', 
    'CreditMemo', 'Deposit', 'PaymentMethod'
  ];

  const fetchConnectionStatus = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await authenticatedFetch(`${API_URL}/api/quickbooks/status`);

      if (response && response.ok) {
        const data = await response.json();
        setConnectionStatus(data);
      } else if (response) {
        throw new Error(`Failed to fetch status: ${response.statusText}`);
      } else {
        throw new Error('Failed to get response from server');
      }
    } catch (err) {
      console.error('Error fetching QuickBooks status:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch QuickBooks status');
    } finally {
      setLoading(false);
    }
  };

  const fetchBarrettConnectionStatus = async () => {
    setBarrettLoading(true);

    try {
      const response = await authenticatedFetch(`${API_URL}/api/barrett-quickbooks/status`);

      if (response && response.ok) {
        const data = await response.json();
        setBarrettConnectionStatus(data);
      } else if (response) {
        console.error('Failed to fetch Barrett status:', response.statusText);
        setBarrettConnectionStatus({ success: false, connected: false });
      } else {
        setBarrettConnectionStatus({ success: false, connected: false });
      }
    } catch (err) {
      console.error('Error fetching Barrett QuickBooks status:', err);
      setBarrettConnectionStatus({ success: false, connected: false });
    } finally {
      setBarrettLoading(false);
    }
  };

  useEffect(() => {
    fetchConnectionStatus();
    fetchBarrettConnectionStatus();
  }, []);

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getDaysUntilExpiry = (expiryDate?: string) => {
    if (!expiryDate) return null;
    const now = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const handleSyncClick = () => {
    setSyncDialogOpen(true);
    setSelectedEntities([]);
    setSyncType('incremental');
  };

  const handleStartSync = async () => {
    setSyncing(true);
    setSyncDialogOpen(false);

    try {
      console.log(`Starting ${syncType} sync with entities:`, selectedEntities.length > 0 ? selectedEntities : 'all');
      
      const response = await authenticatedFetch(`${API_URL}/api/quickbooks/sync/${syncType}`, {
        method: 'POST',
        body: JSON.stringify({
          entities: selectedEntities.length > 0 ? selectedEntities : undefined
        }),
        timeout: 30000 // Increase timeout to 30 seconds for sync operations
      });

      if (response) {
        console.log('Sync response status:', response.status);
        const data = await response.json();
        console.log('Sync response data:', data);
        
        if (data.success && data.operationId) {
          // Start polling for operation status
          pollOperationStatus(data.operationId);
        } else {
          throw new Error(data.error || 'Failed to start sync');
        }
      } else {
        console.error('No response received from sync endpoint');
      }
    } catch (error) {
      console.error('Sync error:', error);
      setError(error instanceof Error ? error.message : 'Failed to start sync');
      setSyncing(false);
    }
  };

  const pollOperationStatus = async (operationId: string) => {
    const interval = setInterval(async () => {
      try {
        const response = await authenticatedFetch(`${API_URL}/api/quickbooks/operations/${operationId}`, {
          timeout: 15000 // 15 second timeout for status checks
        });

        if (response) {
          const data = await response.json();
          
          // Parse progress string to extract numbers (e.g., "Syncing Customer (3/10)...")
          let totalEntities = 1;
          let completedEntities = 0;
          let currentEntity = data.progress;
          
          if (data.progress) {
            const match = data.progress.match(/\((\d+)\/(\d+)\)/);
            if (match) {
              completedEntities = parseInt(match[1], 10) - (data.status === 'processing' ? 1 : 0);
              totalEntities = parseInt(match[2], 10);
            }
          }
          
          // Transform operation status to sync status format for display
          setSyncStatus({
            syncId: operationId,
            status: data.status === 'processing' ? 'in_progress' : data.status,
            startedAt: data.createdAt || new Date().toISOString(),
            completedAt: data.completedAt,
            progress: {
              currentEntity: currentEntity,
              totalEntities: totalEntities,
              completedEntities: data.status === 'completed' ? totalEntities : completedEntities
            }
          });

          if (data.status === 'completed' || data.status === 'failed') {
            clearInterval(interval);
            setSyncing(false);
            
            // Show result or error
            if (data.status === 'failed' && data.error) {
              setError(data.error);
            }
            
            // Refresh connection status
            fetchConnectionStatus();
          }
        }
      } catch (error) {
        console.error('Error polling operation status:', error);
        clearInterval(interval);
        setSyncing(false);
      }
    }, 2000); // Poll every 2 seconds

    // Clear interval after 10 minutes to prevent indefinite polling
    setTimeout(() => {
      clearInterval(interval);
      setSyncing(false);
    }, 600000);
  };

  const handleEntityToggle = (entity: string) => {
    setSelectedEntities(prev => 
      prev.includes(entity) 
        ? prev.filter(e => e !== entity)
        : [...prev, entity]
    );
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Breadcrumb */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            color="inherit"
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => navigate('/')}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
            Home
          </Link>
          <Link
            color="inherit"
            sx={{ cursor: 'pointer' }}
            onClick={() => navigate('/admin/hub')}
          >
            Admin Hub
          </Link>
          <Typography color="text.primary">QuickBooks Integration</Typography>
        </Breadcrumbs>
        
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <CloudSyncIcon sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              QuickBooks Integration
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Manage QuickBooks connection, data synchronization, and sync settings.
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Breadcrumb */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            color="inherit"
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => navigate('/')}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
            Home
          </Link>
          <Link
            color="inherit"
            sx={{ cursor: 'pointer' }}
            onClick={() => navigate('/admin/hub')}
          >
            Admin Hub
          </Link>
          <Typography color="text.primary">QuickBooks Integration</Typography>
        </Breadcrumbs>
        
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <CloudSyncIcon sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              QuickBooks Integration
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Manage QuickBooks connection, data synchronization, and sync settings.
          </Typography>
        </Box>
        
        <Alert severity="error">
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
      </Container>
    );
  }

  const isConnected = connectionStatus?.success && connectionStatus?.connected;
  const daysUntilExpiry = getDaysUntilExpiry(connectionStatus?.refreshTokenExpiresAt);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          color="inherit"
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => navigate('/')}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
          Home
        </Link>
        <Link
          color="inherit"
          sx={{ cursor: 'pointer' }}
          onClick={() => navigate('/admin/hub')}
        >
          Admin Hub
        </Link>
        <Typography color="text.primary">QuickBooks Integration</Typography>
      </Breadcrumbs>
      
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CloudSyncIcon sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              QuickBooks Integration
            </Typography>
          </Box>
          <Button
            startIcon={<RefreshIcon />}
            onClick={fetchConnectionStatus}
            variant="outlined"
            size="small"
          >
            Refresh
          </Button>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Manage QuickBooks connection, data synchronization, and sync settings.
        </Typography>
      </Box>

      <Card>
        <CardContent sx={{ p: 4 }}>

          {/* Connection Status */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              {isConnected ? (
                <>
                  <CheckCircleIcon sx={{ color: 'success.main', fontSize: 32 }} />
                  <Typography variant="h6" color="success.main">
                    Connected
                  </Typography>
                  <Chip 
                    label="Active" 
                    color="success" 
                    size="small"
                    sx={{ ml: 'auto' }}
                  />
                </>
              ) : (
                <>
                  <CancelIcon sx={{ color: 'error.main', fontSize: 32 }} />
                  <Typography variant="h6" color="error.main">
                    Not Connected
                  </Typography>
                  <Chip 
                    label="Inactive" 
                    color="error" 
                    size="small"
                    sx={{ ml: 'auto' }}
                  />
                </>
              )}
            </Box>

            {connectionStatus?.message && (
              connectionStatus.requiresReconnect ? (
                <Alert severity="error" sx={{ ml: 5 }}>
                  {connectionStatus.message}
                </Alert>
              ) : connectionStatus.verified === false ? (
                <Alert severity="warning" sx={{ ml: 5 }}>
                  {connectionStatus.message}
                </Alert>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ ml: 5 }}>
                  {connectionStatus.message}
                </Typography>
              )
            )}
          </Box>

          {/* Sync and View Tables Buttons if connected */}
          {isConnected && (
            <Box sx={{ mb: 3 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={syncing ? <CircularProgress size={20} /> : <SyncIcon />}
                onClick={handleSyncClick}
                disabled={syncing}
                sx={{ mr: 2 }}
              >
                {syncing ? 'Syncing...' : 'Sync Now'}
              </Button>
              
              <Button
                variant="outlined"
                color="primary"
                startIcon={<StorageIcon />}
                onClick={() => navigate('/admin/quickbooks/tables')}
                sx={{ mr: 2 }}
              >
                View Tables
              </Button>
              
              {syncStatus && (
                <Chip
                  label={
                    syncing && syncStatus.progress?.currentEntity
                      ? syncStatus.progress.currentEntity
                      : syncStatus.status === 'completed' 
                        ? 'Sync completed'
                        : syncStatus.status === 'failed'
                          ? 'Sync failed'
                          : `Status: ${syncStatus.status}`
                  }
                  color={syncStatus.status === 'completed' ? 'success' : 
                         syncStatus.status === 'failed' ? 'error' : 'warning'}
                  size="small"
                />
              )}
            </Box>
          )}

          {/* Sync Progress */}
          {syncing && syncStatus && (
            <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" gutterBottom>
                Sync Progress
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={(syncStatus.progress.completedEntities / syncStatus.progress.totalEntities) * 100}
                sx={{ mb: 1 }}
              />
              <Typography variant="body2" color="text.secondary">
                {syncStatus.progress.currentEntity || 'Processing...'}
              </Typography>
              {syncStatus.errors && syncStatus.errors.length > 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  {syncStatus.errors.length} error(s) encountered during sync
                </Alert>
              )}
            </Paper>
          )}

          {/* Connection Details */}
          {isConnected && connectionStatus && (
            <Paper sx={{ p: 3, bgcolor: 'grey.50', mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Connection Details
              </Typography>
              <List dense disablePadding>
                {connectionStatus.companyId && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText 
                      primary="Company ID"
                      secondary={connectionStatus.companyId}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                      secondaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                )}
                
                {connectionStatus.connectedAt && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText 
                      primary="Connected Since"
                      secondary={formatDate(connectionStatus.connectedAt)}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                      secondaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                )}
                
                {connectionStatus.lastUpdated && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText 
                      primary="Last Updated"
                      secondary={formatDate(connectionStatus.lastUpdated)}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                      secondaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                )}
                
                {connectionStatus.refreshTokenExpiresAt && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText 
                      primary="Token Expiry"
                      secondary={
                        <Box component="span">
                          {formatDate(connectionStatus.refreshTokenExpiresAt)}
                          {daysUntilExpiry !== null && (
                            <Chip 
                              label={`${daysUntilExpiry} days`}
                              size="small"
                              color={daysUntilExpiry > 30 ? 'success' : daysUntilExpiry > 7 ? 'warning' : 'error'}
                              sx={{ ml: 1 }}
                            />
                          )}
                        </Box>
                      }
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                      secondaryTypographyProps={{ variant: 'body2', component: 'div' }}
                    />
                  </ListItem>
                )}
                
                {connectionStatus.accessTokenExpired !== undefined && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText 
                      primary="Access Token Status"
                      secondary={
                        <Chip 
                          label={connectionStatus.accessTokenExpired ? 'Expired (will auto-refresh)' : 'Valid'}
                          size="small"
                          color={connectionStatus.accessTokenExpired ? 'warning' : 'success'}
                        />
                      }
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                      secondaryTypographyProps={{ component: 'div' }}
                    />
                  </ListItem>
                )}
              </List>
            </Paper>
          )}

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="contained"
              startIcon={<SettingsIcon />}
              onClick={() => navigate('/admin/hub/accounting/quickbooks/auth')}
            >
              Manage Connection
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/admin/hub')}
            >
              Back to Admin Hub
            </Button>
          </Box>

          {/* Warning Messages */}
          {isConnected && daysUntilExpiry !== null && daysUntilExpiry <= 30 && (
            <Alert 
              severity={daysUntilExpiry <= 7 ? 'error' : 'warning'} 
              sx={{ mt: 3 }}
            >
              <AlertTitle>
                {daysUntilExpiry <= 7 ? 'Urgent' : 'Warning'}
              </AlertTitle>
              Your QuickBooks connection will expire in {daysUntilExpiry} days. 
              Please reconnect soon to avoid service interruption.
            </Alert>
          )}

          {/* Sync Dialog */}
          <Dialog open={syncDialogOpen} onClose={() => setSyncDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CloudDownloadIcon />
                QuickBooks Data Sync
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Sync Type
                </Typography>
                <FormGroup row>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={syncType === 'incremental'}
                        onChange={() => setSyncType('incremental')}
                      />
                    }
                    label="Incremental (Changes Only)"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={syncType === 'full'}
                        onChange={() => setSyncType('full')}
                      />
                    }
                    label="Full Sync (All Data)"
                  />
                </FormGroup>
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Select Entities to Sync (or leave empty for all)
                </Typography>
                <FormGroup>
                  {availableEntities.map(entity => (
                    <FormControlLabel
                      key={entity}
                      control={
                        <Checkbox
                          checked={selectedEntities.includes(entity)}
                          onChange={() => handleEntityToggle(entity)}
                          size="small"
                        />
                      }
                      label={entity}
                    />
                  ))}
                </FormGroup>
              </Box>

              <Alert severity="info" sx={{ mt: 2 }}>
                {syncType === 'incremental'
                  ? 'Incremental sync will only fetch records that have been modified since the last sync.'
                  : 'Full sync will fetch all records and may take several minutes to complete.'}
              </Alert>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSyncDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleStartSync}
                variant="contained"
                color="primary"
                startIcon={<SyncIcon />}
              >
                Start Sync
              </Button>
            </DialogActions>
          </Dialog>
        </CardContent>
      </Card>

      {/* Barrett QuickBooks Integration Card */}
      <Card sx={{ mt: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
            Barrett HTL QuickBooks Integration
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Secondary integration for creating invoices in Barrett HTL's QuickBooks Online.
          </Typography>

          {barrettLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={40} />
            </Box>
          ) : (
            <>
              {/* Barrett Connection Status */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  {barrettConnectionStatus?.connected ? (
                    <>
                      <CheckCircleIcon sx={{ color: 'success.main', fontSize: 28 }} />
                      <Typography variant="subtitle1" color="success.main">
                        Connected
                      </Typography>
                      <Chip
                        label="Active"
                        color="success"
                        size="small"
                        sx={{ ml: 'auto' }}
                      />
                    </>
                  ) : (
                    <>
                      <CancelIcon sx={{ color: 'text.secondary', fontSize: 28 }} />
                      <Typography variant="subtitle1" color="text.secondary">
                        Not Connected
                      </Typography>
                      <Chip
                        label="Inactive"
                        color="default"
                        size="small"
                        sx={{ ml: 'auto' }}
                      />
                    </>
                  )}
                </Box>

                {barrettConnectionStatus?.message && (
                  barrettConnectionStatus.requiresReconnect ? (
                    <Alert severity="error" sx={{ ml: 5 }}>
                      {barrettConnectionStatus.message}
                    </Alert>
                  ) : barrettConnectionStatus.verified === false ? (
                    <Alert severity="warning" sx={{ ml: 5 }}>
                      {barrettConnectionStatus.message}
                    </Alert>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 5 }}>
                      {barrettConnectionStatus.message}
                    </Typography>
                  )
                )}
              </Box>

              {/* Barrett Connection Details */}
              {barrettConnectionStatus?.connected && (
                <Paper sx={{ p: 3, bgcolor: 'grey.50', mb: 3 }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Connection Details
                  </Typography>
                  <List dense disablePadding>
                    {barrettConnectionStatus.companyId && (
                      <ListItem sx={{ px: 0 }}>
                        <ListItemText
                          primary="Company ID"
                          secondary={barrettConnectionStatus.companyId}
                          primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                          secondaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    )}

                    {barrettConnectionStatus.connectedAt && (
                      <ListItem sx={{ px: 0 }}>
                        <ListItemText
                          primary="Connected Since"
                          secondary={formatDate(barrettConnectionStatus.connectedAt)}
                          primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                          secondaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    )}

                    {barrettConnectionStatus.refreshTokenExpiresAt && (
                      <ListItem sx={{ px: 0 }}>
                        <ListItemText
                          primary="Token Expiry"
                          secondary={
                            <Box component="span">
                              {formatDate(barrettConnectionStatus.refreshTokenExpiresAt)}
                              {(() => {
                                const days = getDaysUntilExpiry(barrettConnectionStatus.refreshTokenExpiresAt);
                                if (days !== null) {
                                  return (
                                    <Chip
                                      label={`${days} days`}
                                      size="small"
                                      color={days > 30 ? 'success' : days > 7 ? 'warning' : 'error'}
                                      sx={{ ml: 1 }}
                                    />
                                  );
                                }
                                return null;
                              })()}
                            </Box>
                          }
                          primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                          secondaryTypographyProps={{ variant: 'body2', component: 'div' }}
                        />
                      </ListItem>
                    )}
                  </List>
                </Paper>
              )}

              {/* Barrett Action Button */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant={barrettConnectionStatus?.connected ? 'outlined' : 'contained'}
                  startIcon={<SettingsIcon />}
                  onClick={() => navigate('/admin/quickbooks/barrett/auth')}
                >
                  {barrettConnectionStatus?.connected ? 'Manage Barrett Connection' : 'Connect to Barrett QuickBooks'}
                </Button>
                <Button
                  variant="text"
                  startIcon={<RefreshIcon />}
                  onClick={fetchBarrettConnectionStatus}
                  size="small"
                >
                  Refresh
                </Button>
              </Box>

              {/* Note about Barrett integration */}
              <Alert severity="info" sx={{ mt: 3 }}>
                This integration is for creating invoices in Barrett HTL's QuickBooks Online environment.
                It does not sync data - only invoice creation is supported.
              </Alert>
            </>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};